import React, { useEffect, useState, useRef } from 'react';

import { Home } from '@mui/icons-material';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import FormContent from './FormContent';
import { createBranchPushNotification, getCountForPushNotification, sendPushNotification, getList } from './operations';
import { Breadcrumbs, ModalConfirmation, SubHeader, Footer } from '../../../components';
import './styles.scoped.css';

const utc = require('dayjs/plugin/utc');

dayjs.extend(utc);

const addBranchPushNotifications = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector(state => state.admin?.user);
  const [branches, setBranches] = useState([]);
  const [lobs, setLOBs] = useState([]);
  const [recipientStatuses, setRecipientStatuses] = useState([]);
  const [messageCharCount, setMessageCharCount] = useState(0);
  const messageCharLimit = 150;
  const [subjectCharCount, setSubjectCharCount] = useState(0);
  const subjectCharLimit = 50;
  const [isLoadingBranches, setIsLoadingBranches] = useState(false);
  const [isLoadingRecipientStatus, setIsLoadingRecipientStatus] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showDriversCountModal, setShowDriversCountModal] = useState(false);
  const [isLoadingLOBS, setIsLoadingLOBs] = useState(false);
  const [form, setForm] = useState({
    type: 'Branch',
    subject: '',
    message: '',
    url: '',
    status: 'Pending',
    lob: null,
    branch: null,
    recipientStatus: '',
    approvedBy: user?.name,
    approvedAt: null,
    createdBy: user?.name,
    createdAt: dayjs().utc().format(),
    sentOn: null,
  });
  const [driversToBeNotified, setDriversToBeNotifed] = useState(0);
  const [driversNotified, setDriversNotifed] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingNotifiedNotifications, setIsLoadingNotifiedNotifications] = useState(false);
  const [errors, setErrors] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const hasLOBSelected = useRef();
  const hasBranchSelected = useRef();

  const handleGetRecipientStatuses = async () => {
    setIsLoadingRecipientStatus(true);
    try {
      const responses = await getList('driver-status');
      setRecipientStatuses(responses);
    } catch (e) {
      console.log('e', e);
    }
    setIsLoadingRecipientStatus(false);
  };

  const handleGetBranches = async () => {
    setIsLoadingBranches(true);
    try {
      const responses = await getList('branches');
      setBranches(responses);
    } catch (e) {
      console.log('e', e);
    }
    setIsLoadingBranches(false);
  };

  const handleGetLOB = async () => {
    setIsLoadingLOBs(true);
    try {
      const responses = await getList('line-of-business');
      setLOBs(responses);
    } catch (e) {
      console.log('e', e);
    }
    setIsLoadingLOBs(false);
  };

  const customStyles = width => ({
    control: provided => ({
      ...provided,
      width: width,
      height: '100%',
      minWidth: '300px',
    }),
    menuList: provided => ({
      ...provided,
      maxHeight: '200px',
    }),
    menu: provided => ({
      ...provided,
      zIndex: 100000,
    }),
    multiValue: provided => ({
      ...provided,
      backgroundColor: '#da0f2e',
    }),
    multiValueLabel: provided => ({
      ...provided,
      color: '#ffffff',
    }),
    multiValueRemove: provided => ({
      ...provided,
      color: '#ffffff',
    }),
  });

  const fieldsAreValid = () => {
    let valid = true;
    let formErrorsCopy = { ...errors };

    const requiredFields = ['branch', 'lob', 'message', 'recipientStatus', 'subject'];

    if (!form?.branch) {
      formErrorsCopy = {
        ...formErrorsCopy,
        branch: 'Please provide a valid value.',
      };
      valid = false;
    } else {
      formErrorsCopy = {
        ...formErrorsCopy,
        branch: '',
      };
      valid = true;
    }

    if (!form?.lob) {
      formErrorsCopy = {
        ...formErrorsCopy,
        lob: 'Please provide a valid value.',
      };
      valid = false;
    } else {
      formErrorsCopy = {
        ...formErrorsCopy,
        lob: '',
      };
      valid = true;
    }

    if (!form?.recipientStatus) {
      formErrorsCopy = {
        ...formErrorsCopy,
        recipientStatus: 'Please provide a valid value.',
      };
      valid = false;
    } else {
      formErrorsCopy = {
        ...formErrorsCopy,
        recipientStatus: '',
      };
      valid = true;
    }

    requiredFields.forEach(field => {
      if (!form[field] || form[field] === '') {
        formErrorsCopy = {
          ...formErrorsCopy,
          [field]: 'Please provide a valid value.',
        };
        valid = false;
      }
    });

    setErrors(formErrorsCopy);

    return valid;
  };

  const handleCheckbox = e => {
    if (e.target.checked === true) {
      setForm({
        ...form,
        status: 'Sent',
        sentOn: dayjs().utc().format(),
        approvedBy: user.name,
        approvedAt: dayjs().utc().format(),
      });
    }
    if (e.target.checked !== true) {
      setForm({
        ...form,
        status: 'Pending',
      });
    }
  };

  const confirmBranchNotification = async () => {
    setIsLoading(true);
    if (fieldsAreValid()) {
      if (form?.status === 'Pending') {
        try {
          await createBranchPushNotification(form);
          navigate(-1);
        } catch (e) {
          setShowErrorModal(true);
          console.log('error', e);
        }
      }

      if (form?.status === 'Sent') {
        try {
          await getDriversToBeNotifiedForPushNotifications(form);
        } catch (e) {
          console.log('error', e);
        }
      }
    }
    setIsLoading(false);
  };

  const getDriversToBeNotifiedForPushNotifications = async aForm => {
    try {
      const response = await getCountForPushNotification(aForm);
      if (response?.responseCode === 200) {
        setIsLoading(false);
        setDriversToBeNotifed(response?.driverCount);
        setShowDriversCountModal(true);
      }

      if (response?.responseCode === 404) {
        setIsLoading(false);
        setDriversToBeNotifed(response?.driverCount);
        setShowDriversCountModal(true);
        setForm({
          ...aForm,
          status: 'Pending',
        });
      }
      return response;
    } catch (e) {
      setShowErrorModal(true);
      console.log('error', e);
      return e;
    }
  };

  const sendBulkPushNotification = async aForm => {
    setIsLoadingNotifiedNotifications(true);

    try {
      const response = await sendPushNotification(aForm);

      if (response?.responseCode === 200 && response?.driverCount > 0) {
        setShowDriversCountModal(false);
        setDriversNotifed(response?.driverCount);
        setIsLoadingNotifiedNotifications(false);
        setShowConfirmationModal(true);
        await createBranchPushNotification(aForm);
      }

      if (response?.responseCode === 404 && response?.responseMessage !== 'Success') {
        setDriversToBeNotifed(response?.driverCount);
        setShowDriversCountModal(true);
        setIsLoadingNotifiedNotifications(false);
        setForm({
          ...aForm,
          status: 'Pending',
        });
      }

      if (response?.errors !== undefined && response?.errors[0]?.errorType === 'Lambda:ExecutionTimeoutException') {
        setIsLoading(false);
        setIsLoadingNotifiedNotifications(false);
        setShowConfirmationModal(false);
      }

      return response;
    } catch (e) {
      console.log('error', e);
      setIsLoadingNotifiedNotifications(false);
      setShowDriversCountModal(false);
      setShowErrorModal(true);
    }
  };

  const setField = (fields, value) => {
    if (Array.isArray(fields)) {
      const itemsToAdd = {};
      const newErrors = { ...errors };
      fields.forEach(field => {
        itemsToAdd[field?.fieldName] = field?.fieldValue;
        if (errors && !!errors[field.fieldName]) delete newErrors[field?.fieldName];
      });
      setForm({
        ...form,
        ...itemsToAdd,
      });

      setErrors({
        ...newErrors,
      });
    } else {
      setForm({
        ...form,
        [fields]: value,
      });
      if (errors && !!errors[fields]) {
        setErrors({
          ...errors,
          [fields]: null,
        });
      }
    }
  };

  const getBranches = async query => {
    if (Array.isArray(hasLOBSelected?.current?.props?.value) && !hasLOBSelected.current.props.value.length) {
      setIsLoadingLOBs(true);
      try {
        if (query) {
          const responses = await getList(`line-of-business?Branch=${query}`);
          setLOBs(responses);
        }
      } catch (e) {
        console.log('e', e);
      }
      setIsLoadingLOBs(false);
    }
  };

  const getLOBs = async query => {
    if (Array.isArray(hasBranchSelected?.current?.props?.value) && !hasBranchSelected.current.props.value.length) {
      setIsLoadingBranches(true);
      try {
        if (query) {
          const responses = await getList(`branches?LOB=${query}`);
          if (responses) {
            setBranches(responses);
          }
        }
      } catch (e) {
        console.log('e', e);
      }
      setIsLoadingBranches(false);
    }
  };

  const convertBranchArrayToObject = items => {
    const arr = [];
    let branch = {};

    if (items) {
      String(items)
        .split(', ')
        ?.forEach(i => {
          branch = branches?.find(option => String(option.Branch) === i);
          arr.push({
            label: `${branch?.Branch} - ${branch?.BranchName}`,
            value: branch?.Branch,
          });
        });
    }

    return arr;
  };

  const convertLOBSArrayToObject = items => {
    const arr = [];
    if (items) {
      items.split(',').forEach(i => {
        arr.push({ label: i, value: i });
      });
    }
    return arr;
  };

  const handleCancel = () => {
    setForm({});
    setDriversToBeNotifed(0);
    setDriversNotifed(0);
    navigate(-1);
  };

  useEffect(() => {
    if (form?.branch === '' && form?.lob === '') {
      handleGetBranches();
      handleGetLOB();
    }
  }, [form?.branch, form?.lob]);

  useEffect(() => {
    if (form?.branch === null && form?.lob === null) {
      handleGetBranches();
      handleGetLOB();
    }
    handleGetRecipientStatuses();
  }, []);

  return (
    <>
      <SubHeader
        text="Push Notifications"
        buttons={[
          { icon: <Home sx={{ color: '#fff', fontSize: 30 }} />, onPress: () => navigate('/push-notifications') },
        ]}
      />

      <Breadcrumbs
        wrapStyle={{ margin: 0 }}
        leftTitle="PN Home"
        middleTitle="Branch Push Notifications"
        midItemAction={() => navigate(`../push-notifications/branch-push-notifications`, { replace: true })}
        rightTitle={`${location.pathname.includes('edit') ? 'Edit Branch Notification' : 'Add Branch Notification'}`}
        route="/push-notifications"
        style={{ marginLeft: 30 }}
      />

      <FormContent
        confirmBranchNotification={confirmBranchNotification}
        customStyles={customStyles}
        errors={errors}
        branches={branches}
        lobs={lobs}
        getLOBs={getLOBs}
        getBranches={getBranches}
        location={location}
        isLoadingBranches={isLoadingBranches}
        isLoadingLOBS={isLoadingLOBS}
        headerTitle={`${'Add Branch Notification'}`}
        handleCheckbox={handleCheckbox}
        isLoading={isLoading}
        recipientStatuses={recipientStatuses}
        isLoadingRecipientStatus={isLoadingRecipientStatus}
        form={form}
        setForm={setForm}
        messageCharCount={messageCharCount}
        messageCharLimit={messageCharLimit}
        setField={setField}
        setMessageCharCount={setMessageCharCount}
        setSubjectCharCount={setSubjectCharCount}
        subjectCharCount={subjectCharCount}
        subjectCharLimit={subjectCharLimit}
        handleCancel={handleCancel}
        convertBranchArrayToObject={convertBranchArrayToObject}
        convertLOBSArrayToObject={convertLOBSArrayToObject}
        hasBranchSelected={hasBranchSelected}
        hasLOBSelected={hasLOBSelected}
      />

      <ModalConfirmation
        title="Send Confirmation"
        body={`This will be sent to ${driversToBeNotified} drivers`}
        isOpen={showDriversCountModal}
        primaryButton={driversToBeNotified > 0}
        optionalClose
        primaryButtonTitle="Send"
        secondaryButtonTitle="Cancel"
        isLoading={isLoadingNotifiedNotifications}
        onClick={async () => {
          sendBulkPushNotification(form);
        }}
        secondaryOnClick={() => {
          setForm({
            ...form,
            status: 'Pending',
          });
          setShowDriversCountModal(false);
        }}
      />

      <ModalConfirmation
        title="Error"
        body="There was an error with the push notification. Please contact support."
        primaryButtonTitle="Close"
        onClick={() => {
          setShowErrorModal(false);
          navigate(-1);
        }}
        isOpen={showErrorModal}
        primaryButton
      />

      <ModalConfirmation
        title="Send Confirmation"
        body={`This was sent to ${driversNotified} drivers`}
        isOpen={showConfirmationModal}
        primaryButton={driversNotified > 0}
        primaryButtonTitle="Ok"
        onClick={async () => {
          setShowConfirmationModal(false);
          navigate(-1);
        }}
      />

      <Footer />
    </>
  );
};

export default addBranchPushNotifications;
